import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Text,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FiFileText, FiGlobe, FiLock } from "react-icons/fi";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "private", label: "Private" },
  { key: "shared", label: "Shared" },
];

const formatNoteDate = (value) => {
  if (!value) {
    return "Recently";
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return "Recently";
  }

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

const FilterChip = ({ active, count, label, onClick }) => (
  <Button
    aria-pressed={active}
    onClick={onClick}
    size="sm"
    variant="outline"
    minH="40px"
    px={{ base: 4, md: 5 }}
    borderColor="#30384E"
    borderRadius="full"
    bg={active ? "#30384E" : "transparent"}
    color={active ? "#FFFFFF" : "#C5CEE0"}
    fontWeight="600"
    _hover={{ bg: active ? "#3A4560" : "#1A2138" }}
    _active={{ bg: active ? "#3A4560" : "#222B45" }}
    _focusVisible={{ outline: "2px solid #ED1D24", outlineOffset: "2px" }}
  >
    {label}
    {count !== undefined ? ` ${count}` : ""}
  </Button>
);

const ProfileNoteRow = ({ note, onClick }) => {
  const isShared = note.privacy === "public";
  const thumbnail = note.comic?.thumbnail?.replace("http:", "https:");

  return (
    <Flex
      as="button"
      type="button"
      aria-label={`Open ${note.comic?.title || "comic"} details`}
      onClick={onClick}
      align="flex-start"
      w="100%"
      minH={{ base: "144px", md: "156px" }}
      py={{ base: 4, md: 5 }}
      gap={{ base: 4, md: 5 }}
      textAlign="left"
      borderBottomWidth="1px"
      borderColor="#252E49"
      _hover={{ bg: "#171D31" }}
      _focusVisible={{ outline: "2px solid #ED1D24", outlineOffset: "-2px" }}
    >
      <Center
        flexShrink={0}
        w={{ base: "52px", md: "56px" }}
        h={{ base: "78px", md: "84px" }}
        borderRadius="5px"
        bg="#1A2138"
        overflow="hidden"
      >
        {thumbnail ? (
          <Image alt="" src={thumbnail} w="100%" h="100%" objectFit="cover" />
        ) : (
          <Icon as={FiFileText} color="#58627A" boxSize="6" />
        )}
      </Center>

      <Box flex="1" minW="0">
        <Flex align="center" gap="2">
          <Text
            flex="1"
            minW="0"
            noOfLines={1}
            color="#F7F9FC"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="700"
          >
            {note.comic?.title || "Comic"}
          </Text>
          <Icon
            aria-label={isShared ? "Shared note" : "Private note"}
            as={isShared ? FiGlobe : FiLock}
            flexShrink={0}
            color={isShared ? "#42AAFF" : "#A8B2C8"}
            boxSize="18px"
          />
        </Flex>
        <Text
          mt="2"
          color="#F7F9FC"
          fontSize="15px"
          lineHeight="23px"
          whiteSpace="pre-wrap"
        >
          {note.text}
        </Text>
        <Text mt="2" color="#8F9BB3" fontSize="12px">
          {formatNoteDate(note.createdAt)}
        </Text>
      </Box>
    </Flex>
  );
};

const ProfileNotesList = ({
  notes = [],
  isError,
  isLoading,
  onComicPress,
  onRetry,
}) => {
  const [filter, setFilter] = useState("all");
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const safeNotes = useMemo(() => (Array.isArray(notes) ? notes : []), [notes]);

  const counts = useMemo(
    () => ({
      private: safeNotes.filter((note) => note.privacy === "private").length,
      shared: safeNotes.filter((note) => note.privacy === "public").length,
    }),
    [safeNotes]
  );

  const visibleNotes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return safeNotes.filter((note) => {
      const matchesFilter =
        filter === "all" ||
        (filter === "private" && note.privacy === "private") ||
        (filter === "shared" && note.privacy === "public");
      const matchesSearch =
        !normalizedSearch ||
        `${note.comic?.title || ""} ${note.text || ""}`
          .toLowerCase()
          .includes(normalizedSearch);

      return matchesFilter && matchesSearch;
    });
  }, [filter, safeNotes, search]);

  const toggleSearch = () => {
    setSearchOpen((isOpen) => !isOpen);
    if (searchOpen) {
      setSearch("");
    }
  };

  return (
    <Box>
      <Flex
        align="center"
        minH={{ base: "64px", md: "72px" }}
        px={{ base: 4, md: 8 }}
        gap="2"
        borderBottomWidth="1px"
        borderColor="#252E49"
      >
        <HStack flex="1" minW="0" spacing="2" overflowX="auto" py="2">
          {FILTERS.map((item) => (
            <FilterChip
              active={filter === item.key}
              count={
                item.key === "private"
                  ? counts.private
                  : item.key === "shared"
                  ? counts.shared
                  : undefined
              }
              key={item.key}
              label={item.label}
              onClick={() => setFilter(item.key)}
            />
          ))}
        </HStack>
        <IconButton
          aria-label={searchOpen ? "Close note search" : "Search notes"}
          icon={searchOpen ? <CloseIcon /> : <SearchIcon />}
          onClick={toggleSearch}
          variant="ghost"
          color="#C5CEE0"
          minW="40px"
          _hover={{ bg: "#222B45" }}
          _focusVisible={{ outline: "2px solid #ED1D24", outlineOffset: "2px" }}
        />
      </Flex>

      {searchOpen && (
        <Input
          aria-label="Search notes"
          autoFocus
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search notes"
          mx={{ base: 4, md: 8 }}
          mt="3"
          w="auto"
          borderColor="#30384E"
          _focus={{ borderColor: "#ED1D24", boxShadow: "0 0 0 1px #ED1D24" }}
        />
      )}

      {isLoading ? (
        <Center minH="180px">
          <Text color="#8F9BB3">Loading your notes…</Text>
        </Center>
      ) : isError ? (
        <Flex minH="180px" align="center" justify="center" direction="column">
          <Text color="#A8B2C8">Notes could not be loaded.</Text>
          <Button mt="4" size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </Flex>
      ) : visibleNotes.length ? (
        <Box px={{ base: 4, md: 8 }}>
          {visibleNotes.map((note) => (
            <ProfileNoteRow
              key={note.id}
              note={note}
              onClick={() => onComicPress?.(note)}
            />
          ))}
        </Box>
      ) : (
        <Flex
          minH="180px"
          align="center"
          justify="center"
          direction="column"
          px="6"
        >
          <Icon as={FiFileText} color="#58627A" boxSize="7" />
          <Text mt="4" color="#8F9BB3" textAlign="center">
            {search.trim()
              ? "No notes match your search."
              : filter === "all"
              ? "You have not written any notes yet."
              : `You have no ${filter} notes yet.`}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default ProfileNotesList;
